<template>
  <div class="root-container">
    <RouterLink
      :to="callListLink"
      aria-label="一覧に戻る"
      class="link-icon mx-300"
    >
      <BIcon>
        close
      </BIcon>
    </RouterLink>
    <div class="divider" />
    <component
      :is="prevLink ? 'router-link' : 'span'"
      :to="prevLink || undefined"
      :aria-label="prevLink ? '前へ' : undefined"
      :aria-disabled="prevLink ? undefined : 'true'"
      class="link-icon mx-200"
      :class="{ 'is-disabled': !prevLink }"
    >
      <BIcon>
        chevron_left
      </BIcon>
    </component>
    <component
      :is="nextLink ? 'router-link' : 'span'"
      :to="nextLink || undefined"
      :aria-label="nextLink ? '次へ' : undefined"
      :aria-disabled="nextLink ? undefined : 'true'"
      class="link-icon mx-200"
      :class="{ 'is-disabled': !nextLink }"
    >
      <BIcon>
        chevron_right
      </BIcon>
    </component>
    <div class="divider" />
    <div class="call-info">
      <div>
        {{ zoomPhoneResult?.calleeDidNumber }}
      </div>
      <div class="call-target-name">
        {{ zoomPhoneResult?.callerUser?.name }}
      </div>
    </div>
    <div class="call-time">
      <BIcon
        type="gray"
      >
        call
      </BIcon>
      <div>
        {{ callTime }}
      </div>
    </div>
    <div class="menu-container">
      <BMoreMenu>
        <template #default>
          <TranscriptionDeleteButton
            :zoom-phone-result="zoomPhoneResult"
          />
        </template>
      </BMoreMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, type RouteLocationRaw } from 'vue-router';
import { ZoomPhoneResult } from '@/api/openapi';
import { formatShorterDate, formatShortTime } from '@/utils/date-time';
import TranscriptionDeleteButton from './TranscriptionDeleteButton.vue';

type TProps = {
  zoomPhoneResult: ZoomPhoneResult | null;
  prevLink: RouteLocationRaw | null;
  nextLink: RouteLocationRaw | null;
}

const props = defineProps<TProps>();
const route = useRoute();
const callListLink = computed(() => ({ name: 'VaCallList', query: route.query }));

const callTime = computed(() => {
  const startedAtDate = formatShorterDate(props.zoomPhoneResult?.startedAt);
  const startedAtTime = formatShortTime(props.zoomPhoneResult?.startedAt);
  const endedAtTime = formatShortTime(props.zoomPhoneResult?.endedAt);
  return `${startedAtDate} ${startedAtTime} - ${endedAtTime}`;
});
</script>

<style lang="scss" scoped>
.root-container {
  display: flex;
  align-items: center;
  width: 100%;

  padding-block: 20px;
  font-family: "Noto Sans JP";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.link-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  text-decoration: none;
  color: $textcolor-base;
  background: transparent;
  border: none;

  &:hover {
    background-color: $bgcolor-dark;
  }

  :deep(.b-icon) {
    font-size: $fontsize-400;
  }
}

.link-icon.is-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.call-info {
  display: flex;
  gap: 14px;
  color: #2B2B2B;
  margin-inline: 12px
}

.call-target-name {
  position: relative;
}

.call-time {
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: normal;

  color: $textcolor-light;
}

.divider {
  width: 2px;
  height: 32px;
  border-right: 1px solid #E3E3E3;
}

.menu-container {
  margin-left: auto;
  margin-right: 16px;
}
</style>
